import { type FC, useContext, useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Flex,
  Form,
  FormContext,
  GoogleAutocomplete,
  googlePlaceToAddress,
  IInputProps,
  Spinner,
  useGoogleApiLoader,
} from 'dash-ui';

import { FormFields } from 'containers/properties/PropertyGeneralInformation/components/PropertyGeneralInfoForm/constants/constants';
import { useGetUsStatesQuery } from 'gql/generated.graphql';

const PlaceSelect: FC<{ defaultPlaceId?: string } & IInputProps> = ({
  defaultPlaceId,
  ...inputProps
}) => {
  const { t } = useTranslation(['forms', 'common', 'pages']);
  const isGoogleApiReady = useGoogleApiLoader();
  // FIXME: re-check
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const geocoder = isGoogleApiReady ? new google.maps.Geocoder() : null;
  const { data: statesData } = useGetUsStatesQuery();
  const states = useMemo(() => statesData?.getUSStates || [], [statesData]);

  const { formErrors, stateHandlers } = useContext(FormContext);
  const [defaultViewValue, setDefaultViewValue] = useState('');
  const onPlaceSelected = (place: google.maps.places.PlaceResult) => {
    const address = googlePlaceToAddress(place);

    const addressState = {
      isoCode: states.find(({ name }) => name === address.state)?.code,
      name: address.state,
    };

    stateHandlers[FormFields.Place](place);
    stateHandlers[FormFields.Street](address.street);
    stateHandlers[FormFields.City](address.city);
    stateHandlers[FormFields.ZipCode](address.zipCode);
    stateHandlers[FormFields.State](addressState);
    stateHandlers[FormFields.County](address.county);
  };

  useEffect(() => {
    if (defaultPlaceId && geocoder) {
      geocoder.geocode({ placeId: defaultPlaceId }, (result) => {
        if (result?.length) {
          setDefaultViewValue(result[0].formatted_address);
        }
      });
    }
  }, [defaultPlaceId, geocoder]);

  return (
    <Form.Group w="100%" mb={3}>
      <Flex>
        <Form.Label required>{t('forms:labels.address')}</Form.Label>
      </Flex>

      {isGoogleApiReady ? (
        <GoogleAutocomplete
          defaultValue={defaultViewValue}
          onPlaceSelected={onPlaceSelected}
          placeholder={t('forms:labels:address') || ''}
          $errorType={formErrors[FormFields.Place]?.type}
          {...inputProps}
        />
      ) : (
        <Spinner />
      )}
      <Form.Feedback $errorType={formErrors[FormFields.Place]?.type}>
        {formErrors[FormFields.Place]?.message}
      </Form.Feedback>
    </Form.Group>
  );
};

export default PlaceSelect;
